<!--
 * @Description: 付费测试模块 -- PC端
 * @Author: Cursor
 * @Date: 2026-04-02
-->
<template>
	<section class="paid-test-section">
		<h2 class="paid-test-section__title">{{ title }}</h2>
		<div class="paid-test-section__card">
			<article
				v-for="item in paidTestList"
				:key="item.id"
				class="paid-test-item"
				@click="toGoLink(getItemUrl(item), schannel)"
			>
				<div class="paid-test-item__media">
					<img
						class="paid-test-item__cover"
						:src="item.cover || item.pc_cover || item.cover_cc"
						:alt="getItemTitle(item)"
					/>
					<div class="paid-test-item__price">
						<img :src="priceIcon" alt="" />
						<span>{{ formatPrice(item) }}</span>
					</div>
				</div>
				<div class="paid-test-item__content">
					<h3 class="paid-test-item__name">{{ getItemTitle(item) }}</h3>
					<p class="paid-test-item__desc">{{ formatDesc(item) }}</p>
					<div class="paid-test-item__footer">
						<button
							class="paid-test-item__button"
							type="button"
							@click.stop="toGoLink(getItemUrl(item), schannel)"
						>
							{{ item.button_text || "立即測試" }}
						</button>
					</div>
				</div>
			</article>
		</div>
	</section>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import api from "~/api/api";
import { goLink } from "~/utils/common";
import priceIcon from "../../images/logo_icon.png";

const DEFAULT_PRICE = "29.9";
const DEFAULT_DESC = "你的睡眠透露出你怎樣的心理訴求?";

const props = defineProps({
	data: {
		type: Object,
		default: () => [],
	},
	title: {
		type: String,
		default: "付費測試",
	},
	schannel: {
		type: String,
		default: "",
	},
});

const apiPaidTestList = ref([]);

const fallbackPaidTestList = computed(() =>
	(Array.isArray(props.data) ? props.data : []).filter(Boolean).slice(0, 6)
);

const paidTestList = computed(() =>
	apiPaidTestList.value.length ? apiPaidTestList.value : fallbackPaidTestList.value
);

function resolvePaidTestList(res) {
	const data = res?.data;
	if (Array.isArray(data)) return data;
	if (Array.isArray(data?.list)) return data.list;
	if (Array.isArray(data?.data)) return data.data;
	if (Array.isArray(data?.data?.list)) return data.data.list;
	return [];
}

async function fetchPaidTestList() {
	try {
		const res = await api.getForecastZone({
			v: 2,
			page: 1,
			size: 6,
			is_test: 1,
		});
		apiPaidTestList.value = resolvePaidTestList(res).slice(0, 6);
	} catch (error) {
		console.error("获取付费测试列表失败:", error);
	}
}

onMounted(() => {
	fetchPaidTestList();
});

function formatPrice(item) {
	const rawPrice =
		item?.price || item?.final_price || item?.discount_price || item?.unit_price || item?.min_price;

	if (rawPrice === undefined || rawPrice === null || rawPrice === "") {
		return DEFAULT_PRICE;
	}

	return String(rawPrice).replace(/[^\d.]/g, "") || DEFAULT_PRICE;
}

function formatCurrency(item) {
	return item?.currency_symbol || "";
}

function formatDesc(item) {
	return item?.description || item?.sub_title || item?.subtitle || DEFAULT_DESC;
}

function getItemTitle(item) {
	return item?.title || item?.name || "";
}

function getItemUrl(item) {
	return item?.url || item?.jump_url || "";
}

function toGoLink(url, schannel) {
	if (schannel === "calc-emotion-pc") {
		logEvent("home_Emotional_click");
	}

	goLink(url, schannel);
}
</script>

<style lang="scss" scoped>
.paid-test-section {
	width: 281px;
	min-height: 642px;
	display: flex;
	flex-direction: column;
	font-family: PingFang SC;

	&__title {
		margin: 0 0 12px;
		font-weight: 500;
		font-size: 20px;
		line-height: 28px;
		color: #1f2329;
	}

	&__card {
		flex: 1;
		padding: 8px 14px 8px;
		background: #ffffff;
		border: 1px solid #ececec;
		border-radius: 12px;
	}
}

.paid-test-item {
	display: flex;
	gap: 10px;
	padding: 6px 0;
	border-bottom: 1px solid #f0f0f0;
	cursor: pointer;

	&:last-child {
		border-bottom: none;
	}

	&__cover {
		width: 55px;
		height: 55px;
		object-fit: cover;
		border-radius: 8px;
		margin-top: 5px;
	}

	&__media {
		flex-shrink: 0;
		width: 60px;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
	}

	&__content {
		min-width: 0;
		flex: 1;
		padding-top: 2px;
	}

	&__name {
		margin: 0;
		font-family: Helvetica Neue;
		font-weight: 500;
		font-size: 16px;
		line-height: 22px;
		color: #2b2f36;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__desc {
		margin: 4px 0 0;
		font-size: 12px;
		line-height: 15px;
		height: 30px;
		color: #4a4a4a;
		display: -webkit-box;
		line-clamp: 2;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	&__footer {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 8px;
		margin-top: 8px;
	}

	&__price {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		margin-top: 16px;
		font-size: 14px;
		line-height: 20px;
		color: #ff5e48;

		img {
			width: 16px;
			height: 16px;
			flex-shrink: 0;
		}
	}

	&__button {
		flex-shrink: 0;
		width: 76px;
		height: 28px;
		padding: 0;
		border: none;
		border-radius: 8px;
		background: #343434;
		font-weight: 500;
		font-size: 12px;
		line-height: 28px;
		color: #ffffff;
		text-align: center;
		cursor: pointer;
	}
}
</style>
